import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Check, Shield, Zap, DollarSign, LayoutGrid, Server, Smartphone, RefreshCcw, Headphones, Activity, MessageSquare, User, CheckCircle, ArrowRight, Star, Settings, Database, Sliders, Layers, MonitorPlay } from 'lucide-react';

// Import assets
// import amyImg from '../assets/amy.png';

const VoIPPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Communication Suite</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold leading-tight tracking-tight">
                Modern VoIP Designed <br />
                <span className="text-blue-600">Exclusively for Dental</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Stop using archaic systems built for general businesses like plumbing and HVAC. Switch to the only communication platform fully integrated with your dental AI workforce.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <button className="px-6 py-3 rounded-xl font-bold text-sm transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-200 hover:shadow-blue-300 transform hover:-translate-y-0.5">
                  Get a demo
                </button>
                <button className="px-6 py-3 rounded-xl font-bold text-sm transition-all bg-white text-gray-700 hover:bg-gray-50 border border-gray-200">
                  See pricing
                </button>
              </div>

              <div className="flex items-center gap-4 text-sm text-gray-500 pt-4">
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Seamless Porting</span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Free Hardware Options</span>
                </div>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative flex items-center justify-center">
                <div className="relative w-full max-w-md aspect-[4/3] bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2rem] shadow-2xl p-4 ring-1 ring-gray-900/50">
                    {/* Screen Content */}
                    <div className="absolute inset-2 bg-slate-900 rounded-[1.5rem] overflow-hidden">
                        {/* Header */}
                        <div className="h-14 bg-slate-800 flex items-center justify-between px-6 border-b border-slate-700">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            </div>
                            <div className="text-slate-400 text-xs">Dental VoIP Dashboard</div>
                        </div>
                        
                        {/* Main Content */}
                        <div className="p-6 grid gap-4">
                            {/* Incoming Call Card */}
                            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700 animate-pulse ring-1 ring-blue-500/50">
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white">
                                            <Phone className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <div className="text-white font-semibold">Incoming Call</div>
                                            <div className="text-blue-400 text-sm">Sarah Jenkins</div>
                                        </div>
                                    </div>
                                    <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full">Live</span>
                                </div>
                                
                                {/* Patient Info Pop-up (Dental Context) */}
                                <div className="bg-slate-700/50 rounded-lg p-3 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-400">Next Appt:</span>
                                        <span className="text-white">Tomorrow, 2:00 PM</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-400">Balance:</span>
                                        <span className="text-red-400">$150.00 (Overdue)</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-400">Insurance:</span>
                                        <span className="text-white">Delta Dental</span>
                                    </div>
                                </div>
                            </div>

                            {/* Recent Activity */}
                            <div className="space-y-2">
                                <div className="text-slate-500 text-xs font-bold uppercase tracking-wider">Recent Activity</div>
                                <div className="flex items-center gap-3 p-2 hover:bg-slate-800/50 rounded-lg transition-colors">
                                    <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center">
                                        <MessageSquare className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-slate-300 text-sm">Appt Reminder Sent</div>
                                        <div className="text-slate-500 text-xs">To: Mark Wilson</div>
                                    </div>
                                    <div className="text-slate-500 text-xs">2m ago</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Floating Elements */}
                    <div className="absolute -right-6 top-20 bg-white p-4 rounded-xl shadow-xl border border-gray-100 animate-float" style={{ animationDelay: '1s' }}>
                        <div className="flex items-center gap-3">
                            <div className="bg-green-100 p-2 rounded-lg">
                                <Activity className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <div className="text-xs text-gray-500">System Status</div>
                                <div className="text-sm font-bold text-gray-900">99.99% Uptime</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* REFINED SECTION: Why Settle for Generic? (Bento Grid) */}
      <section className="py-24 px-6 bg-white border-t border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-16 max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Why settle for <span className="text-gray-400">Generic</span> when <br />
              you can have <span className="text-blue-600">Specialized?</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Other companies like Mango and Weave serve every industry from auto shops to hair salons. We've spent our time perfecting communication specifically for dental.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1 - Designed for Dental (Span 2) */}
            <div className="md:col-span-2 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10 max-w-lg">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-blue-600">
                  <Activity className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Designed for Dental</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Our features are built around your workflows. Patient pop-ups show relevant dental data like remaining benefits, treatment plans, and recall status, not just generic contact info.
                </p>
              </div>
              
              {/* Graphic Overlay */}
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[45%] h-full hidden md:flex items-center justify-center p-6">
                 <div className="relative w-full max-w-xs">
                     <div className="glass-card bg-white/80 p-4 rounded-xl shadow-lg mb-3 transform translate-x-4 border border-white/50">
                         <div className="flex items-center gap-3 mb-2">
                             <div className="bg-blue-100 p-1.5 rounded-lg"><Activity className="w-3 h-3 text-blue-600" /></div>
                             <span className="text-xs font-bold text-gray-700">Patient Pop-Up</span>
                         </div>
                         <div className="space-y-2">
                             <div className="flex justify-between text-xs">
                                <span className="text-gray-500">Hygiene Due:</span>
                                <span className="text-red-500 font-semibold">Overdue 2mo</span>
                             </div>
                             <div className="flex justify-between text-xs">
                                <span className="text-gray-500">Remaining Benefits:</span>
                                <span className="text-green-600 font-semibold">$1,250</span>
                             </div>
                         </div>
                     </div>
                 </div>
              </div>
              
              <div className="absolute right-0 bottom-0 w-1/2 h-full bg-gradient-to-l from-blue-50/50 to-transparent -z-10"></div>
            </div>

            {/* Card 2 - Unified Platform (Span 1) */}
            <div className="md:col-span-1 bg-white rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600">
                  <LayoutGrid className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Unified Platform</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Stop managing multiple logins. Your VoIP, AI Receptionist, and Patient Communication all live in one dashboard.
                </p>
              </div>
            </div>

            {/* Card 3 - Cost Savings (Span 1) */}
            <div className="md:col-span-1 bg-gray-50 rounded-[2rem] p-8 overflow-hidden relative group border border-gray-100 transition-all hover:shadow-lg">
              <div className="relative z-10">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-6 shadow-sm text-green-600">
                  <DollarSign className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Massive Cost Savings</h3>
                <p className="text-gray-600 leading-relaxed">
                  Save a ton of cost compared to solutions like Weave. VoIP is often included with agent bundles.
                </p>
              </div>
            </div>

            {/* Card 4 - Full Dashboard (Span 2) */}
             <div className="md:col-span-2 bg-slate-900 rounded-[2rem] p-8 overflow-hidden relative group transition-all hover:shadow-xl text-white">
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                    <div className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center mb-6 text-blue-400">
                      <MonitorPlay className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">Seamless Porting</h3>
                    <p className="text-slate-400 leading-relaxed mb-4">
                      Dreading the switch? Don't be. We've streamlined the porting process to a single form. We manage everything else to ensure you don't experience a single second of downtime.
                    </p>
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400" />
                            <span>1-Form Process</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400" />
                            <span>Zero Downtime</span>
                        </div>
                    </div>
                </div>
                
                {/* Visual */}
                <div className="hidden md:block w-64">
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700/50">
                        <div className="flex items-center gap-3 mb-3">
                            <RefreshCcw className="w-5 h-5 text-blue-400" />
                            <span className="text-sm font-bold text-white">Porting Status</span>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <div className="flex justify-between text-xs text-slate-400 mb-1">
                                    <span>Main Line</span>
                                    <span className="text-green-400">Completed</span>
                                </div>
                                <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                                    <div className="h-full w-full bg-green-500 rounded-full"></div>
                                </div>
                            </div>
                             <div>
                                <div className="flex justify-between text-xs text-slate-400 mb-1">
                                    <span>Fax Line</span>
                                    <span className="text-blue-400">In Progress</span>
                                </div>
                                <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
                                    <div className="h-full w-3/4 bg-blue-500 rounded-full animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
              </div>
               <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:16px_16px]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Hardware Section (Kept mostly the same but refined style) */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
                <div className="bg-blue-50 rounded-[2rem] p-8 min-h-[400px] flex items-center justify-center relative overflow-hidden">
                    {/* Abstract Phone Illustration */}
                    <div className="relative z-10 bg-white p-6 rounded-2xl shadow-xl w-64 text-center border border-gray-100 transform hover:scale-105 transition-transform duration-500">
                        <Smartphone className="w-16 h-16 text-gray-800 mx-auto mb-4" />
                        <h4 className="font-bold text-gray-900 mb-1">Top-Tier Hardware</h4>
                        <p className="text-sm text-gray-500">Yealink T54W & More</p>
                        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-center gap-2">
                             <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                             <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                             <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                        </div>
                    </div>
                    {/* Decor */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-blue-200/20 rounded-full blur-3xl"></div>
                </div>
            </div>

            <div className="order-1 lg:order-2 space-y-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Premium Hardware <br />
                  <span className="text-blue-600">Included.</span>
                </h2>
                <p className="text-lg text-gray-600">
                  We ship pre-configured, top-of-the-line phones directly to your office. Just plug them in and start making calls.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 text-blue-600">
                        <Smartphone className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="font-bold text-gray-900">Latest Models</h4>
                        <p className="text-sm text-gray-600">Access to the latest Yealink and Polycom devices.</p>
                    </div>
                </div>

                <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 text-blue-600">
                        <Headphones className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="font-bold text-gray-900">White-Glove Setup</h4>
                        <p className="text-sm text-gray-600">Our team ensures your staff is trained and comfortable from day one.</p>
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dentina Style Match */}
      <section className="py-14 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-600 to-blue-700 shadow-xl border border-blue-500/50">
            <div className="grid lg:grid-cols-2 gap-0 items-center">
              {/* Left: Content */}
              <div className="p-10 lg:p-12 relative z-10">
                <p className="text-xs font-bold text-blue-100 tracking-wider mb-3 uppercase">
                  EVERYTHING TOGETHER IN ONE PLACE
                </p>
                
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white leading-tight">
                  Unlock special bundle pricing when you switch.
                </h2>
                
                <p className="text-base text-blue-100 mb-6 leading-relaxed max-w-xl">
                  Combine your VoIP with our AI Receptionist and Front Office Agents to simplify your tech stack and supercharge your practice. Save a ton of cost compared to using separate providers.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                    <button className="group px-7 py-3 rounded-xl font-bold text-sm transition-all bg-white text-blue-600 hover:bg-blue-50 shadow-lg hover:shadow-xl">
                    <span className="flex items-center gap-2">
                        Check Bundle Availability
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                    </button>
                    <button className="px-7 py-3 rounded-xl font-bold text-sm transition-all bg-transparent border border-white/30 text-white hover:bg-white/10">
                        Talk to Sales
                    </button>
                </div>
              </div>

              {/* Right: Graphic Composition */}
              <div className="relative h-full min-h-[400px] flex items-center justify-center p-8 overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white/10 rounded-full"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-white/5 rounded-full"></div>
                </div>

                {/* Main Card */}
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-100 p-6 w-72 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Unified Dashboard</span>
                    </div>
                  </div>

                  {/* Icon Area */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-xl flex flex-col items-center justify-center gap-2">
                        <Phone className="w-6 h-6 text-blue-600" />
                        <span className="text-xs font-bold text-gray-700">VoIP</span>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-xl flex flex-col items-center justify-center gap-2">
                        <User className="w-6 h-6 text-purple-600" />
                        <span className="text-xs font-bold text-gray-700">Agents</span>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                      <span className="text-xs font-semibold text-gray-600">Total Savings: <span className="text-green-600 font-bold">$450/mo</span></span>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute top-20 right-10 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform rotate-6 animate-float">
                  <DollarSign className="w-5 h-5 text-green-500" />
                </div>
                <div className="absolute bottom-20 left-10 bg-white p-3 rounded-xl shadow-lg border border-gray-100 transform -rotate-6 animate-float" style={{ animationDelay: '1s' }}>
                  <LayoutGrid className="w-5 h-5 text-indigo-500" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VoIPPage;

































